import React, { useState, useEffect } from 'react';
import MDEditor from '@uiw/react-md-editor';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';

const General = ({ procedureData, onCancel }) => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState([]);
  const [showNewCategory, setShowNewCategory] = useState(false);
  const [editingProcedureId, setEditingProcedureId] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [showNewSubCategory, setShowNewSubCategory] = useState(false);
  const [newSubCategoryName, setNewSubCategoryName] = useState('');
  const [types, setTypes] = useState([]);
  const [showNewTypeInput, setShowNewTypeInput] = useState(false);
  const [newTypeName, setNewTypeName] = useState('');
  const [userLabId, setUserLabId] = useState('');

  const [formData, setFormData] = useState({
    name: '',
    script: '',
    customId: '',
    quickCode: '',
    category: '',
    newCategoryName: '',
    subCategory: '',
    type: '',
    procedureGroups: '',
    procedureCategory: '',
    procedureDepartment: '',
    sortRank: '',
    unitCount: '',
    tariffCode: '',
    labOnly: false,
    inactive: false,
    salesDescription: '',
    newGroup: '',
    trafficCode: '',
    description: '',
    overrideDefault: false,
    turnAroundDays: '',
    ignoreInbound: false,
    ignoreOutbound: false,
    autoArrive: 'no',
    placeOnHold: 'no',
    treatmentLength: '',
    isLotRequired: false,
    allowNoteForLot: false,
    isSerialRequired: false,
    allowNoteForSerial: false,
    traceabilityValue: '',
  });

  // Function to show success alert
  const showSuccessAlert = (isUpdate = false) => {
    Swal.fire({
      title: 'Success!',
      text: isUpdate ? 'Procedure updated successfully!' : 'Procedure created successfully!',
      icon: 'success',
      confirmButtonText: 'OK',
      confirmButtonColor: '#004aad',
      customClass: {
        popup: 'custom-swal-popup',
        title: 'custom-swal-title',
        confirmButton: 'custom-swal-confirm-button'
      }
    });
  };

  // Function to show error alert
  const showErrorAlert = (errorMessage) => {
    Swal.fire({
      title: 'Error!',
      text: errorMessage,
      icon: 'error',
      confirmButtonText: 'OK',
      confirmButtonColor: '#dc3545'
    });
  };

  // Function to show confirmation alert for cancel
  const showCancelConfirmation = () => {
    Swal.fire({
      title: 'Clear Form Data?',
      text: 'Do you want to clear the current form data? If you cancel, the data will remain for editing.',
      icon: 'question',
      showCancelButton: true,
      confirmButtonText: 'Yes, clear it!',
      cancelButtonText: 'No, keep it',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then((result) => {
      if (result.isConfirmed) {
        resetForm();
        if (onCancel) {
          onCancel();
        }
      }
    });
  };

  // Get userLabId from localStorage on component mount
  useEffect(() => {
    const getUserLabId = () => {
      try {
        // Check localStorage for userLabId
        const userLabIdFromStorage = localStorage.getItem('userLabId');
        
        if (userLabIdFromStorage) {
          console.log('Found userLabId in localStorage:', userLabIdFromStorage);
          setUserLabId(userLabIdFromStorage);
          return userLabIdFromStorage;
        }
        
        // If not found, try to get from userData
        const userData = localStorage.getItem('userData');
        if (userData) {
          try {
            const parsedData = JSON.parse(userData);
            console.log('Parsed userData:', parsedData);
            
            if (parsedData.labId) {
              console.log('Found labId in userData:', parsedData.labId);
              setUserLabId(parsedData.labId);
              localStorage.setItem('userLabId', parsedData.labId);
              return parsedData.labId;
            }
            
            if (parsedData.userLabId) {
              console.log('Found userLabId in userData:', parsedData.userLabId);
              setUserLabId(parsedData.userLabId);
              localStorage.setItem('userLabId', parsedData.userLabId);
              return parsedData.userLabId;
            }
            
            if (parsedData.id) {
              console.log('Found id in userData:', parsedData.id);
              setUserLabId(parsedData.id);
              localStorage.setItem('userLabId', parsedData.id);
              return parsedData.id;
            }
          } catch (parseError) {
            console.error('Error parsing userData:', parseError);
          }
        }
        
        // Check for other possible keys
        const possibleKeys = ['labId', 'userId', 'labUserId', 'currentLabId'];
        for (const key of possibleKeys) {
          const value = localStorage.getItem(key);
          if (value) {
            console.log(`Found ${key} in localStorage:`, value);
            setUserLabId(value);
            localStorage.setItem('userLabId', value);
            return value;
          }
        }
        
        console.warn('userLabId not found in localStorage');
        console.log('All localStorage items:');
        for (let i = 0; i < localStorage.length; i++) {
          const key = localStorage.key(i);
          console.log(`${key}: ${localStorage.getItem(key)}`);
        }
        
        return '';
      } catch (error) {
        console.error('Error getting userLabId from localStorage:', error);
        return '';
      }
    };
    
    const labId = getUserLabId();
    if (!labId) {
      console.error('Could not retrieve userLabId from localStorage.');
    }
  }, []);

  // Fetch categories based on userLabId
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        let url = 'https://hms.backend.hiragroupcloud.com/api/categories';
        
        if (userLabId) {
          url += `?userLabId=${encodeURIComponent(userLabId)}`;
        }
        
        console.log('Fetching categories from:', url);
        
        const response = await axios.get(url);
        console.log('Raw categories response:', response.data);
        
        setCategories(response.data);
      } catch (error) {
        console.error('Error fetching categories:', error);
        setCategories([]);
      }
    };

    if (userLabId) {
      fetchCategories();
    } else {
      console.log('Waiting for userLabId before fetching categories...');
      setCategories([]);
    }
  }, [userLabId]);

  // Fetch sub-categories based on selected category AND userLabId
  useEffect(() => {
    const fetchSubCategories = async () => {
      if (formData.category && formData.category !== 'New Category') {
        try {
          let url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}`;
          
          if (userLabId) {
            url += `&userLabId=${encodeURIComponent(userLabId)}`;
          }
          
          console.log('Fetching sub-categories from:', url);
          
          const response = await axios.get(url);
          console.log('Raw sub-categories response:', response.data);
          
          const subCatNames = response.data.map(item => item.name);
          
          console.log('Sub-categories for current user:', subCatNames);
          setSubCategories(subCatNames);
        } catch (error) {
          console.error('Error fetching sub-categories:', error);
          setSubCategories([]);
        }
      } else {
        setSubCategories([]);
      }
    };

    if (formData.category && userLabId) {
      fetchSubCategories();
    } else {
      setSubCategories([]);
    }
  }, [formData.category, userLabId]);

  // Fetch types based on selected sub-category AND userLabId
  useEffect(() => {
    const fetchTypes = async () => {
      if (formData.subCategory && userLabId) {
        try {
          let url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}`;
          
          if (userLabId) {
            url += `&userLabId=${encodeURIComponent(userLabId)}`;
          }
          
          console.log('Fetching types from:', url);
          
          const response = await axios.get(url);
          console.log('Raw types response:', response.data);
          
          const typeNames = response.data.map(item => item.name);
          
          console.log('Types for current user:', typeNames);
          setTypes(typeNames);
        } catch (error) {
          console.error('Error fetching types:', error);
          setTypes([]);
        }
      } else {
        setTypes([]);
      }
    };

    if (formData.subCategory && userLabId) {
      fetchTypes();
    } else {
      setTypes([]);
    }
  }, [formData.subCategory, userLabId]);

  // Load procedure data from sessionStorage
  useEffect(() => {
    const savedProcedureId = sessionStorage.getItem('procedureId');
    if (savedProcedureId && !procedureData) {
      fetchProcedureData(savedProcedureId);
    }
  }, []);

  const fetchProcedureData = async (procedureId) => {
    try {
      const response = await axios.get(`https://hms.backend.hiragroupcloud.com/api/procedures/${procedureId}`);
      const procedure = response.data;
      
      setFormData({
        name: procedure.name || '',
        script: procedure.script || '',
        customId: procedure.customId || '',
        quickCode: procedure.quickCode || '',
        category: procedure.category || '',
        newCategoryName: procedure.newCategoryName || '',
        subCategory: procedure.subCategory || '',
        type: procedure.type || '',
        procedureCategory: procedure.procedureCategory || '',
        procedureGroups: procedure.procedureGroups || '',
        procedureDepartment: procedure.procedureDepartment || '',
        sortRank: procedure.sortRank || '',
        unitCount: procedure.unitCount || '',
        tariffCode: procedure.tariffCode || '',
        labOnly: procedure.labOnly || false,
        inactive: procedure.inactive || false,
        salesDescription: procedure.salesDescription || '',
        newGroup: procedure.newGroup || '',
        trafficCode: procedure.trafficCode || '',
        description: procedure.description || '',
        overrideDefault: procedure.overrideDefault || false,
        turnAroundDays: procedure.turnAroundDays || '',
        ignoreInbound: procedure.ignoreInbound || false,
        ignoreOutbound: procedure.ignoreOutbound || false,
        autoArrive: procedure.autoArrive || 'no',
        placeOnHold: procedure.placeOnHold || 'no',
        treatmentLength: procedure.treatmentLength || '',
        isLotRequired: procedure.isLotRequired || false,
        allowNoteForLot: procedure.allowNoteForLot || false,
        isSerialRequired: procedure.isSerialRequired || false,
        allowNoteForSerial: procedure.allowNoteForSerial || false,
        traceabilityValue: procedure.traceabilityValue || '',
      });
      setShowNewCategory(procedure.category === 'New Category');
      setEditingProcedureId(procedure._id || null);
    } catch (error) {
      console.error('Error fetching procedure data:', error);
    }
  };

  useEffect(() => {
    if (procedureData) {
      setFormData({
        name: procedureData.name || '',
        script: procedureData.script || '',
        customId: procedureData.customId || '',
        quickCode: procedureData.quickCode || '',
        category: procedureData.category || '',
        newCategoryName: procedureData.newCategoryName || '',
        subCategory: procedureData.subCategory || '',
        type: procedureData.type || '',
        procedureCategory: procedureData.procedureCategory || '',
        procedureGroups: procedureData.procedureGroups || '',
        procedureDepartment: procedureData.procedureDepartment || '',
        sortRank: procedureData.sortRank || '',
        unitCount: procedureData.unitCount || '',
        tariffCode: procedureData.tariffCode || '',
        labOnly: procedureData.labOnly || false,
        inactive: procedureData.inactive || false,
        salesDescription: procedureData.salesDescription || '',
        newGroup: procedureData.newGroup || '',
        trafficCode: procedureData.trafficCode || '',
        description: procedureData.description || '',
        overrideDefault: procedureData.overrideDefault || false,
        turnAroundDays: procedureData.turnAroundDays || '',
        ignoreInbound: procedureData.ignoreInbound || false,
        ignoreOutbound: procedureData.ignoreOutbound || false,
        autoArrive: procedureData.autoArrive || 'no',
        placeOnHold: procedureData.placeOnHold || 'no',
        treatmentLength: procedureData.treatmentLength || '',
        isLotRequired: procedureData.isLotRequired || false,
        allowNoteForLot: procedureData.allowNoteForLot || false,
        isSerialRequired: procedureData.isSerialRequired || false,
        allowNoteForSerial: procedureData.allowNoteForSerial || false,
        traceabilityValue: procedureData.traceabilityValue || '',
      });
      setShowNewCategory(procedureData.category === 'New Category');
      setEditingProcedureId(procedureData._id || null);
    }
  }, [procedureData]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
  };

  const handleCategoryChange = (e) => {
    const { value } = e.target;
    setFormData({
      ...formData,
      category: value,
      newCategoryName: value === 'New Category' ? formData.newCategoryName : '',
      subCategory: '',
      type: '',
    });
    setShowNewCategory(value === 'New Category');
    setSubCategories([]);
    setTypes([]);
  };

  const handleQuillChange = (value) => {
    setFormData((prevData) => ({
      ...prevData,
      salesDescription: value,
    }));
  };

  const handleCheckboxChange = (e) => {
    if (e.target.checked) {
      setFormData({
        ...formData,
        overrideDefault: true,
      });
    } else {
      Swal.fire({
        title: 'Delete Custom Scheduling Rules?',
        text: 'Are you sure you want to delete your custom scheduling rules?',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Yes, delete them!',
        cancelButtonText: 'No, keep them',
        confirmButtonColor: '#3085d6',
        cancelButtonColor: '#d33',
      }).then((result) => {
        if (result.isConfirmed) {
          setFormData({
            ...formData,
            overrideDefault: false,
            turnAroundDays: '',
          });
        } else {
          setFormData({
            ...formData,
            overrideDefault: true,
          });
        }
      });
    }
  };

  const handleAddSubCategory = async () => {
    if (!newSubCategoryName.trim()) {
      showErrorAlert('Please enter a sub-category name.');
      return;
    }

    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }

    try {
      console.log('Adding sub-category with data:', {
        name: newSubCategoryName,
        category: formData.category,
        userLabId: userLabId
      });

      const response = await axios.post('https://hms.backend.hiragroupcloud.com/api/sub-categories', {
        name: newSubCategoryName,
        category: formData.category,
        addedBy: userLabId, // CHANGED: Changed userLabId to addedBy
        userLabId: userLabId // Keeping both for compatibility
      });

      console.log('Sub-category added successfully:', response.data);
      
      // Refresh sub-categories list
      const url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}&userLabId=${userLabId}`;
      const refreshResponse = await axios.get(url);
      const subCatNames = refreshResponse.data.map(item => item.name);
      
      setSubCategories(subCatNames);
      setFormData({ ...formData, subCategory: newSubCategoryName });
      setNewSubCategoryName('');
      setShowNewSubCategory(false);
      
      Swal.fire({
        title: 'Success!',
        text: 'Sub-category added successfully!',
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#004aad'
      });
      
    } catch (error) {
      console.error('Error adding sub-category:', error);
      if (error.response && error.response.status === 400) {
        showErrorAlert(error.response.data.error);
      } else {
        showErrorAlert('Failed to add sub-category. Please try again.');
      }
    }
  };

  const handleAddType = async () => {
    if (!newTypeName.trim()) {
      showErrorAlert('Please enter a type name.');
      return;
    }

    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }

    try {
      console.log('Adding type with data:', {
        name: newTypeName,
        subCategory: formData.subCategory,
        userLabId: userLabId
      });

      const response = await axios.post('https://hms.backend.hiragroupcloud.com/api/types', {
        name: newTypeName,
        subCategory: formData.subCategory,
        addedBy: userLabId, // CHANGED: Changed userLabId to addedBy
        userLabId: userLabId // Keeping both for compatibility
      });

      console.log('Type added successfully:', response.data);
      
      // Refresh types list
      const url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}&userLabId=${userLabId}`;
      const refreshResponse = await axios.get(url);
      const typeNames = refreshResponse.data.map(item => item.name);
      
      setTypes(typeNames);
      setFormData({ ...formData, type: newTypeName });
      setNewTypeName('');
      setShowNewTypeInput(false);
      
      Swal.fire({
        title: 'Success!',
        text: `Type "${newTypeName}" added successfully!`,
        icon: 'success',
        confirmButtonText: 'OK',
        confirmButtonColor: '#004aad'
      });
      
    } catch (error) {
      console.error('Error adding type:', error);
      if (error.response && error.response.status === 400) {
        showErrorAlert(`Error: ${error.response.data.error}`);
      } else {
        showErrorAlert('Failed to add type. Please try again.');
      }
    }
  };

  const handleDeleteSubCategory = async () => {
    if (!formData.subCategory) {
      showErrorAlert('Please select a sub-category to delete.');
      return;
    }
    
    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }
    
    Swal.fire({
      title: 'Delete Sub-Category?',
      text: `Are you sure you want to delete ${formData.subCategory}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `https://hms.backend.hiragroupcloud.com/api/sub-categories/${formData.subCategory}?category=${formData.category}&userLabId=${userLabId}`
          );
          
          const url = `https://hms.backend.hiragroupcloud.com/api/sub-categories?category=${encodeURIComponent(formData.category)}&userLabId=${userLabId}`;
          const refreshResponse = await axios.get(url);
          const subCatNames = refreshResponse.data.map(item => item.name);
          
          setSubCategories(subCatNames);
          setFormData({ ...formData, subCategory: '', type: '' });
          setTypes([]);
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Sub-category deleted successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#004aad'
          });
        } catch (error) {
          console.error('Error deleting sub-category:', error);
          showErrorAlert('Failed to delete sub-category. Please try again.');
        }
      }
    });
  };

  const handleDeleteType = async () => {
    if (!formData.type) {
      showErrorAlert('Please select a type to delete.');
      return;
    }
    
    if (!userLabId) {
      showErrorAlert('User identification required. Please login again.');
      return;
    }
    
    Swal.fire({
      title: 'Delete Type?',
      text: `Are you sure you want to delete ${formData.type}?`,
      icon: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Yes, delete it!',
      cancelButtonText: 'No, cancel',
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
    }).then(async (result) => {
      if (result.isConfirmed) {
        try {
          await axios.delete(
            `https://hms.backend.hiragroupcloud.com/api/types/${formData.type}?subCategory=${formData.subCategory}&userLabId=${userLabId}`
          );
          
          const url = `https://hms.backend.hiragroupcloud.com/api/types?subCategory=${encodeURIComponent(formData.subCategory)}&userLabId=${userLabId}`;
          const refreshResponse = await axios.get(url);
          const typeNames = refreshResponse.data.map(item => item.name);
          
          setTypes(typeNames);
          setFormData({ ...formData, type: '' });
          
          Swal.fire({
            title: 'Deleted!',
            text: 'Type deleted successfully!',
            icon: 'success',
            confirmButtonText: 'OK',
            confirmButtonColor: '#004aad'
          });
        } catch (error) {
          console.error('Error deleting type:', error);
          showErrorAlert('Failed to delete type. Please try again.');
        }
      }
    });
  };

  const resetForm = () => {
    setFormData({
      name: '',
      script: '',
      customId: '',
      quickCode: '',
      category: '',
      newCategoryName: '',
      subCategory: '',
      type: '',
      procedureGroups: '',
      procedureCategory: '',
      procedureDepartment: '',
      sortRank: '',
      unitCount: '',
      tariffCode: '',
      labOnly: false,
      inactive: false,
      salesDescription: '',
      newGroup: '',
      trafficCode: '',
      description: '',
      overrideDefault: false,
      turnAroundDays: '',
      ignoreInbound: false,
      ignoreOutbound: false,
      autoArrive: 'no',
      placeOnHold: 'no',
      treatmentLength: '',
      isLotRequired: false,
      allowNoteForLot: false,
      isSerialRequired: false,
      allowNoteForSerial: false,
      traceabilityValue: '',
    });
    setShowNewCategory(false);
    setEditingProcedureId(null);
    setShowNewSubCategory(false);
    setNewSubCategoryName('');
    setShowNewTypeInput(false);
    setNewTypeName('');
    setSubCategories([]);
    setTypes([]);
    sessionStorage.removeItem('procedureId');
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    
    // Show loading alert
    Swal.fire({
      title: 'Please wait...',
      text: 'Saving procedure data',
      allowOutsideClick: false,
      showConfirmButton: false,
      willOpen: () => {
        Swal.showLoading();
      }
    });
    
    // Get userLabId directly from localStorage at submission time
    let currentUserLabId = localStorage.getItem('userLabId');
    
    // If not found as userLabId, try labId
    if (!currentUserLabId) {
      currentUserLabId = localStorage.getItem('labId');
    }
    
    // If still not found, try to extract from userData
    if (!currentUserLabId) {
      const userData = localStorage.getItem('userData');
      if (userData) {
        try {
          const parsedData = JSON.parse(userData);
          currentUserLabId = parsedData.labId || parsedData.userLabId || parsedData.id;
        } catch (e) {
          console.error('Error parsing userData:', e);
        }
      }
    }
    
    console.log('Submitting with userLabId:', currentUserLabId);
    
    if (!currentUserLabId && !editingProcedureId) {
      Swal.close();
      showErrorAlert('User identification (userLabId) is required. Please login again.');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        sortRank: formData.sortRank ? parseInt(formData.sortRank) : 0,
        turnAroundDays: formData.turnAroundDays ? parseInt(formData.turnAroundDays) : '',
        userLabId: currentUserLabId, // Add userLabId to the payload
        addedBy: currentUserLabId // Also add addedBy for compatibility
      };
      
      console.log('Submitting procedure with payload:', payload);
      
      let response;
      
      if (editingProcedureId) {
        response = await axios.put(
          `https://hms.backend.hiragroupcloud.com/api/procedures/${editingProcedureId}`,
          payload
        );
        
        // Close loading alert and show success
        Swal.close();
        showSuccessAlert(true); // true means it's an update
        
      } else {
        response = await axios.post('https://hms.backend.hiragroupcloud.com/api/procedures', payload);

        if (response.data && response.data._id) {
          sessionStorage.setItem('procedureId', response.data._id);
          setEditingProcedureId(response.data._id);
        }
        
        // Close loading alert and show success
        Swal.close();
        showSuccessAlert(false); // false means it's a create
      }
      
      console.log('Procedure saved successfully:', response.data);
      
    } catch (error) {
      console.error('Error submitting form:', error);
      Swal.close();
      
      if (error.response && error.response.data && error.response.data.error) {
        showErrorAlert(`Failed to submit form: ${error.response.data.error}`);
      } else {
        showErrorAlert('Failed to submit form. Please try again.');
      }
    }
  };

  const handleCancel = () => {
    showCancelConfirmation();
  };

  const sectionStyle = {
    backgroundColor: '#f0f0f0',
    borderTop: '2px solid #004aad',
    padding: '20px',
    margin: '10px 0',
    boxSizing: 'border-box',
  };

  const labelStyle = {
    fontWeight: 'bold',
    marginRight: '10px',
    color: 'black',
    width: '20%',
  };

  const inputStyle = {
    width: 'calc(50% - 160px)',
    padding: '8px',
    fontSize: '15px',
    borderRadius: '6px',
    border: '1px solid #ccc',
    marginLeft: '55px',
  };

  const checkboxStyle = {
    marginLeft: '55px',
  };

  const inputRowStyle = {
    marginBottom: '15px',
    display: 'flex',
    alignItems: 'center',
    flexWrap: 'wrap',
  };

  return (
    <div style={{ width: '100%' }}>
      <style>
        {`
          .custom-swal-popup {
            border-radius: 10px;
            border: 2px solid #004aad;
            box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
          }
          
          .custom-swal-title {
            color: #004aad;
            font-weight: bold;
            font-size: 20px;
          }
          
          .custom-swal-confirm-button {
            background-color: #004aad !important;
            border-radius: 5px;
            padding: 8px 20px;
          }
          
          .custom-swal-confirm-button:hover {
            background-color: #003580 !important;
          }
          
          .swal2-icon.swal2-success {
            border-color: #004aad;
            color: #004aad;
          }
          
          .swal2-icon.swal2-success .swal2-success-ring {
            border: 4px solid rgba(0, 74, 173, 0.2);
          }
          
          .swal2-icon.swal2-success [class^='swal2-success-line'] {
            background-color: #004aad;
          }
          
          .swal2-icon.swal2-error {
            border-color: #dc3545;
            color: #dc3545;
          }
          
          .swal2-icon.swal2-error [class^='swal2-x-mark-line'] {
            background-color: #dc3545;
          }
          
          .swal2-icon.swal2-warning {
            border-color: #ffc107;
            color: #ffc107;
          }
          
          .swal2-icon.swal2-question {
            border-color: #004aad;
            color: #004aad;
          }
        `}
      </style>
      
      <form onSubmit={handleSubmit}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem' }}>
          <h4 style={{ color: '#80aaff', margin: 0 }}>Procedure Classification</h4>
          <div>
            <button
              type="submit"
              style={{
                padding: '8px 16px',
                borderRadius: '5px',
                border: 'none',
                backgroundColor: '#007bff',
                color: 'white',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
              disabled={!editingProcedureId && !userLabId}
              title={!editingProcedureId && !userLabId ? "User identification required" : ""}
            >
              {editingProcedureId ? 'Update' : 'Save'}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              style={{
                marginLeft: '10px',
                padding: '8px 16px',
                borderRadius: '5px',
                border: '1px solid #dc3545',
                backgroundColor: 'white',
                color: '#dc3545',
                cursor: 'pointer',
                fontWeight: 'bold'
              }}
            >
              Cancel
            </button>
          </div>
        </div>
        <div style={sectionStyle}>
          <div style={inputRowStyle}>
            <label htmlFor="category" style={labelStyle}>
              Category <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="category"
              name="category"
              value={formData.category}
              onChange={handleCategoryChange}
              style={inputStyle}
              required
              disabled={!userLabId}
            >
              <option value="">Select Category</option>
              <option value="Fixed Restoration">Fixed Restoration</option>
              <option value="Removable Restoration">Removable Restoration</option>
              <option value="Orthodontic Appliance">Orthodontic Appliance</option>
              <option value="Implant Restoration">Implant Restoration</option>
              <option value="Sundry">Sundry</option>
            </select>
            {!userLabId && (
              <span style={{ marginLeft: '10px', color: '#dc3545', fontSize: '12px' }}>
                Login required to load categories
              </span>
            )}
            {userLabId && categories.length === 0 && (
              <span style={{ marginLeft: '10px', color: '#6c757d', fontSize: '12px' }}>
                No categories found for your lab. You can create new ones.
              </span>
            )}
          </div>
          {formData.category && (
            <div style={inputRowStyle}>
              <label htmlFor="subCategory" style={labelStyle}>
                Sub-Category <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="subCategory"
                name="subCategory"
                value={formData.subCategory}
                onChange={handleChange}
                style={inputStyle}
                required
                disabled={!userLabId}
              >
                <option value="">Select Sub-Category</option>
                {subCategories.map((subCat) => (
                  <option key={subCat} value={subCat}>
                    {subCat}
                  </option>
                ))}
              </select>
              {formData.subCategory && (
                <button
                  type="button"
                  onClick={handleDeleteSubCategory}
                  style={{
                    marginLeft: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowNewSubCategory(true)}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#004aaf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
                disabled={!userLabId}
              >
                Add
              </button>
            </div>
          )}
          {showNewSubCategory && (
            <div style={{ ...inputRowStyle, marginLeft: '25%' }}>
              <input
                type="text"
                value={newSubCategoryName}
                onChange={(e) => setNewSubCategoryName(e.target.value)}
                placeholder="Enter new sub-category"
                style={{ ...inputStyle, width: '200px', marginLeft: '0' }}
              />
              <button
                type="button"
                onClick={handleAddSubCategory}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#004aad',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewSubCategory(false);
                  setNewSubCategoryName('');
                }}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
          {formData.subCategory && (
            <div style={inputRowStyle}>
              <label htmlFor="type" style={labelStyle}>
                Type <span style={{ color: 'red' }}>*</span>
              </label>
              <select
                id="type"
                name="type"
                value={formData.type}
                onChange={handleChange}
                style={inputStyle}
                required
                disabled={!userLabId}
              >
                <option value="">Select Type</option>
                {types.map((type) => (
                  <option key={type} value={type}>
                    {type}
                  </option>
                ))}
              </select>
              {formData.type && (
                <button
                  type="button"
                  onClick={handleDeleteType}
                  style={{
                    marginLeft: '10px',
                    padding: '8px 12px',
                    backgroundColor: '#dc3545',
                    color: 'white',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer',
                  }}
                >
                  Delete
                </button>
              )}
              <button
                type="button"
                onClick={() => setShowNewTypeInput(true)}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#004aaf',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
                disabled={!userLabId}
              >
                Add
              </button>
            </div>
          )}
          {showNewTypeInput && (
            <div style={{ ...inputRowStyle, marginLeft: '25%' }}>
              <input
                type="text"
                value={newTypeName}
                onChange={(e) => setNewTypeName(e.target.value)}
                placeholder="Enter new type"
                style={{ ...inputStyle, width: '200px', marginLeft: '0' }}
              />
              <button
                type="button"
                onClick={handleAddType}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#004aad',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Save
              </button>
              <button
                type="button"
                onClick={() => {
                  setShowNewTypeInput(false);
                  setNewTypeName('');
                }}
                style={{
                  marginLeft: '10px',
                  padding: '8px 12px',
                  backgroundColor: '#6c757d',
                  color: 'white',
                  border: 'none',
                  borderRadius: '5px',
                  cursor: 'pointer',
                }}
              >
                Cancel
              </button>
            </div>
          )}
        </div>
       
        <h4 style={{ color: '#80aaff' }}>Display</h4>
        <div style={sectionStyle}>
          <div style={inputRowStyle}>
            <label htmlFor="name" style={labelStyle}>
              Name <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="script" style={labelStyle}>
              Description <span style={{ color: 'red' }}>*</span>
            </label>
            <input
              type="text"
              id="script"
              name="script"
              value={formData.script}
              onChange={handleChange}
              style={inputStyle}
              required
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="customId" style={labelStyle}>CustomID</label>
            <input
              type="text"
              id="customId"
              name="customId"
              value={formData.customId}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="quickCode" style={labelStyle}>Quick Code</label>
            <input
              type="text"
              id="quickCode"
              name="quickCode"
              value={formData.quickCode}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="procedureCategory" style={labelStyle}>
              Category <span style={{ color: 'red' }}>*</span>
            </label>
            <select
              id="procedureCategory"
              name="procedureCategory"
              value={formData.procedureCategory}
              onChange={handleChange}
              style={inputStyle}
              required
            >
              <option value="">Select Category</option>
              {categories.length > 0 ? (
                categories.map((cat) => (
                  <option key={cat._id} value={cat.name}>
                    {cat.name}
                  </option>
                ))
              ) : (
                <option value="" disabled>No categories available</option>
              )}
            </select>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="procedureGroups" style={labelStyle}>Procedure Group</label>
            <select
              id="procedureGroups"
              name="procedureGroups"
              value={formData.procedureGroups}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select Option</option>
              <option value="Group1">New Group</option>
            </select>
          </div>
          {formData.procedureGroups === 'Group1' && (
            <div style={inputRowStyle}>
              <label htmlFor="newGroup" style={labelStyle}>
                New Group <span style={{ color: 'red' }}>*</span>
              </label>
              <input
                type="text"
                id="newGroup"
                name="newGroup"
                value={formData.newGroup}
                onChange={handleChange}
                style={inputStyle}
                required
              />
            </div>
          )}
          <div style={inputRowStyle}>
            <label htmlFor="procedureDepartment" style={labelStyle}>Procedure Department</label>
            <select
              id="procedureDepartment"
              name="procedureDepartment"
              value={formData.procedureDepartment}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select Procedure Department</option>
              <option value="Department1">Department 1</option>
              <option value="Department2">Department 2</option>
            </select>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="sortRank" style={labelStyle}>Sort Rank</label>
            <input
              type="number"
              id="sortRank"
              name="sortRank"
              value={formData.sortRank}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="unitCount" style={labelStyle}>Unit Count</label>
            <select
              id="unitCount"
              name="unitCount"
              value={formData.unitCount}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="">Select Unit Count</option>
              <option value="Count by Teeth">Count by Teeth</option>
              <option value="Count by Units">Count by Units</option>
            </select>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="trafficCode" style={labelStyle}>Traffic Code</label>
            <input
              type="text"
              id="trafficCode"
              name="trafficCode"
              value={formData.trafficCode}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="labOnly" style={labelStyle}>Lab Only</label>
            <input
              type="checkbox"
              id="labOnly"
              name="labOnly"
              checked={formData.labOnly}
              onChange={handleChange}
              style={checkboxStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="inactive" style={labelStyle}>Inactive</label>
            <input
              type="checkbox"
              id="inactive"
              name="inactive"
              checked={formData.inactive}
              onChange={handleChange}
              style={checkboxStyle}
            />
          </div>
          <p style={{ fontWeight: 'bold', fontSize: '18px' }}>Sale Description</p>
          <p style={{ marginTop: '10px', color: 'black' }}>
            Provide an optional extended sales-oriented description about this procedure.
          </p>
          <MDEditor
            theme="snow"
            value={formData.salesDescription}
            onChange={handleQuillChange}
            placeholder="Enter your ad description here..."
            style={{ marginTop: '10px', height: '200px', width: '100%', marginBottom: '5%' }}
          />
        </div>

        <h4 style={{ color: '#80aaff' }}>Scheduling</h4>
        <div style={sectionStyle}>
          <div style={{ marginBottom: '10px', display: 'flex' }}>
            <label style={{ fontWeight: 'bold', color: 'black', width: '27%' }}>
              Default Turn Around
            </label>
            <p style={{ color: 'black', display: 'inline' }}>
              {formData.overrideDefault && formData.turnAroundDays ? (
                <>
                  {formData.turnAroundDays} day(s)
                  <hr style={{ marginTop: '5px', marginBottom: '10px', border: '1px solid #ccc' }} />
                </>
              ) : (
                '10 day(s)'
              )}
            </p>
          </div>
          <div>
            <div>
              <label htmlFor="overrideDefault" style={{ fontWeight: 'bold' }}>
                Override Default
              </label>
              <input
                type="checkbox"
                id="overrideDefault"
                name="overrideDefault"
                checked={formData.overrideDefault}
                onChange={handleCheckboxChange}
                style={{ marginLeft: '130px' }}
              />
            </div>
            {formData.overrideDefault && (
              <div style={{ marginLeft: '250px', marginTop: '10px' }}>
                <div style={{ marginBottom: '10px' }}>
                  <label style={{ fontSize: '14px', marginRight: '50px' }}>
                    Turn Around Days
                  </label>
                  <input
                    type="number"
                    name="turnAroundDays"
                    value={formData.turnAroundDays}
                    onChange={handleChange}
                    style={{ padding: '5px', width: '150px', marginRight: '10px', borderRadius: '5px' }}
                  />
                </div>
              </div>
            )}
          </div>
          <div style={{ marginBottom: '20px', display: 'flex' }}>
            <label style={{ fontWeight: 'bold', color: '#004aad' }}>
              Select Tasks
            </label>
          </div>
          <div style={{ marginBottom: '20px' }}>
            <p style={{ fontStyle: 'italic', color: '#555' }}>
              By ignoring inbound transit days you are indicating that this is a digital procedure and that files are required to be uploaded.
            </p>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="ignoreInbound" style={labelStyle}>Ignore Inbound Transit Days</label>
            <input
              type="checkbox"
              id="ignoreInbound"
              name="ignoreInbound"
              checked={formData.ignoreInbound}
              onChange={handleChange}
              style={checkboxStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="ignoreOutbound" style={labelStyle}>Ignore Outbound Transit Days</label>
            <input
              type="checkbox"
              id="ignoreOutbound"
              name="ignoreOutbound"
              checked={formData.ignoreOutbound}
              onChange={handleChange}
              style={checkboxStyle}
            />
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="autoArrive" style={labelStyle}>Auto Arrive</label>
            <select
              id="autoArrive"
              name="autoArrive"
              value={formData.autoArrive}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="placeOnHold" style={labelStyle}>Place on Hold</label>
            <select
              id="placeOnHold"
              name="placeOnHold"
              value={formData.placeOnHold}
              onChange={handleChange}
              style={inputStyle}
            >
              <option value="yes">Yes</option>
              <option value="no">No</option>
            </select>
          </div>
          <div style={inputRowStyle}>
            <label htmlFor="treatmentLength" style={labelStyle}>Treatment Length</label>
            <input
              type="text"
              id="treatmentLength"
              name="treatmentLength"
              value={formData.treatmentLength}
              onChange={handleChange}
              style={inputStyle}
            />
          </div>
        </div>

        <h4 style={{ color: '#80aaff' }}>Traceability</h4>
        <div style={{ ...sectionStyle, borderTop: '1px solid #000' }}>
          <div style={{ display: 'flex' }}>
            <label style={{ fontWeight: 'bold', width: '28%' }}>
              Description of material, or part
            </label>
            <input
              type="text"
              placeholder="Enter Value Name"
              name="traceabilityValue"
              value={formData.traceabilityValue}
              onChange={handleChange}
              style={{
                width: 'calc(50% - 160px)',
                padding: '8px',
                fontSize: '15px',
                borderRadius: '4px',
                border: '1px solid #ccc',
              }}
            />
          </div>
          <br />
          <div style={{ display: 'flex', alignItems: 'center' }}>
            <label style={{ width: '18%', fontWeight: 'bold' }}>
              Lot #
            </label>
            <div style={{ marginLeft: '10%' }}>
              <input
                type="checkbox"
                checked={formData.isLotRequired}
                onChange={handleChange}
                name="isLotRequired"
              />
              <label>Is Required</label>
            </div>
            <div style={{ marginLeft: '10px' }}>
              <input
                type="checkbox"
                checked={formData.allowNoteForLot}
                onChange={handleChange}
                name="allowNoteForLot"
                disabled={!formData.isLotRequired}
              />
              <label>Allow Note</label>
            </div>
          </div>
          <div style={{ display: 'flex', alignItems: 'center', marginTop: '15px' }}>
            <label style={{ width: '20%', fontWeight: 'bold' }}>
              Serial #
            </label>
            <div style={{ marginLeft: '8%' }}>
              <input
                type="checkbox"
                checked={formData.isSerialRequired}
                onChange={handleChange}
                name="isSerialRequired"
              />
              <label>Is Required</label>
            </div>
            <div style={{ marginLeft: '10px' }}>
              <input
                type="checkbox"
                checked={formData.allowNoteForSerial}
                onChange={handleChange}
                name="allowNoteForSerial"
                disabled={!formData.isSerialRequired}
              />
              <label>Allow Note</label>
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

export default General;